import './styles.scoped.css'
import {useState, useEffect, useRef} from 'react'
import {
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    Button,
    Avatar,
    Link,
    Autocomplete,
    Chip,
    IconButton,
    Skeleton,
} from '@mui/material'
import {UploadFile, Upload, Edit, Delete, Cancel} from '@mui/icons-material'
import {Controller} from 'react-hook-form'
import {usStates} from '../../../../utils/helpers'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {clientApi, accountApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {useParams} from 'react-router-dom'
import {parseError, ignoreTimezone} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {useDropzone} from 'react-dropzone'
import {useMobile} from '../../../../hooks'
import {AddressAutocompleteField, SkeletonContainer, FieldSkeleton, ButtonSkeleton, TextSkeleton} from '../../../../components'

const AddressSkeleton = () => (
    <>
        <FieldSkeleton labelWidth={140} />
        <br />
        <FieldSkeleton labelWidth={140} />
        <br />
        <div className="form" style={{marginBottom: 0}}>
            <FieldSkeleton labelWidth={40} sx={{flex: 1}} />
            <FieldSkeleton labelWidth={50} sx={{flex: 1}} />
        </div>
        <br />
        <FieldSkeleton labelWidth={70} />
    </>
)

const PersonalInformationSkeleton = ({mobile}) => (
    <SkeletonContainer>
        <FieldSkeleton labelWidth={130} />
        <div className="selected-items-container">
            <Skeleton variant="rounded" height={40} sx={{borderRadius: '100px'}} />
        </div>
        <br />
        <hr className="line" />
        <br />
        <TextSkeleton width={100} />
        {mobile ? <ButtonSkeleton width={150} /> : <Skeleton variant="rounded" width="50%" height={150} sx={{minWidth: 300, borderRadius: 0.25}} />}
        <br />
        <div className="form" style={{marginBottom: 0}}>
            <FieldSkeleton labelWidth={80} sx={{flex: 1}} />
            <FieldSkeleton labelWidth={80} sx={{flex: 1}} />
        </div>
        <br />
        <FieldSkeleton labelWidth={90} />
        <br />
        <FieldSkeleton labelWidth={60} />
        <br />
        <FieldSkeleton labelWidth={100} />
        <br />
        <hr className="line" />
        <br />
        <Typography variant="overline" color="text.secondary">
            ADDRESS
        </Typography>
        <br />
        <AddressSkeleton />
        <br />
        <hr className="line" />
        <br />
        <Typography variant="overline" color="text.secondary">
            EVV LOCATION
        </Typography>
        <br />
        <div style={{display: 'flex', alignItems: 'center', gap: 12, height: 42}}>
            <Skeleton variant="rounded" width={18} height={18} sx={{ml: 1.5, borderRadius: 0.5}} />
            <TextSkeleton width={180} fontSize={16} />
        </div>
        <br />
        <AddressSkeleton />
        <br />
        <br />
        <div className="actions">
            <ButtonSkeleton width={80} />
            <ButtonSkeleton width={140} />
        </div>
    </SkeletonContainer>
)

export default () => {
    const [sameAsTheAddressAbove, setSameAsTheAddressAbove] = useState(false)
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {clientId} = useParams()
    const {mobile} = useMobile()
    const [branches, setBranches] = useState([])
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const [selectedBranches, setSelectedBranches] = useState([])
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                branch_ids: yup.array().of(yup.string()),
                branch_ids_helper: selectedBranches?.length > 0 ? yup.string().notRequired() : yup.string().required('Branches is required'),
                primary_branch_id: yup.string(),
                firstName: yup.string().required('First Name is required'),
                lastName: yup.string().required('Last Name is required'),
                maNumber: yup
                    .string()
                    .required('MA Number is required')
                    .matches(/^\d+$/, 'MA Number must contain only numbers')
                    .min(8, 'MA Number must be between 8 and 12 characters')
                    .max(12, 'MA Number must be between 8 and 12 characters'),
                gender: yup.string().required('Gender is required'),
                dateOfBirth: yup.date().required('Date of Birth is required'),
                primaryAddressId: yup.string(),
                primaryAddressStreetAddressLine1: yup.string().required('Address Line 1 is required'),
                primaryAddressStreetAddressLine2: yup.string(),
                primaryAddressCity: yup.string().required('City is required'),
                primaryAddressState: yup.string().required('State is required'),
                primaryAddressZipCode: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
                primaryAddressLatitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                primaryAddressLongitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                sameAsTheAddressAbove: yup.boolean().default(false),
                evvAddressId: yup.string(),
                evvAddressStreetAddressLine1: yup.string().required('Address Line 1 is required'),
                evvAddressStreetAddressLine2: yup.string(),
                evvAddressCity: yup.string().required('City is required'),
                evvAddressState: yup.string().required('State is required'),
                evvAddressZipCode: yup
                    .string()
                    .required('Zip Code is required')
                    .min(5, 'Zip Code must be between 5 and 20 characters')
                    .max(20, 'Zip Code must be between 5 and 20 characters')
                    .matches(/^\d+$/, 'Zip Code must contain only numbers'),
                evvAddressLatitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
                evvAddressLongitude: yup
                    .number()
                    .nullable()
                    .transform((value, originalValue) => (originalValue === '' ? null : value)),
            }),
        ),
    })
    const [file, setFile] = useState(null)
    const {getRootProps, getInputProps, isDragActive, open} = useDropzone({
        // No SVG: it can carry script, and the mobile app can't render it.
        accept: {'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/gif': ['.gif'], 'image/webp': ['.webp']},
        noClick: true,
        multiple: false,
        maxSize: 3 * 1024 * 1024,
        onDrop: acceptedFiles => {
            setFile(acceptedFiles[0])
            setAvatarUrl(URL.createObjectURL(acceptedFiles[0]))
        },
    })
    const [avatarUrl, setAvatarUrl] = useState(null)

    const genders = [
        {key: 'male', value: 'Male'},
        {key: 'female', value: 'Female'},
        {key: 'other', value: 'Other'},
        {key: 'prefer_not_to_say', value: 'Prefer Not to Say'},
    ]

    useEffect(() => {
        accountApi.getBranches().then(
            ({
                data: {
                    data: {branches},
                },
            }) => {
                setBranches(branches)
            },
        )
    }, [])

    useEffect(() => {
        reset(true)
    }, [branches])

    useEffect(() => {
        form.setValue(
            'branch_ids',
            selectedBranches.map(lang => lang.id),
        )
        if (selectedBranches?.length == 1 && !selectedBranches[0].is_primary) {
            selectAsPrimary(selectedBranches[0].id)
            form.setValue('primary_branch_id', selectedBranches[0].id)
        } else {
            form.setValue('primary_branch_id', selectedBranches.find(a => a.is_primary)?.id)
        }
    }, [selectedBranches])

    useEffect(() => {
        if (!file) return
        const formData = new FormData()
        formData.append('file', file)
        setLoading(true)
        clientApi
            .uploadClientPhoto(clientId, formData)
            .then(({data: {data}}) => {
                setLoading(false)
                setAvatarUrl(data?.avatar_url || null)
            })
            .catch(error => {
                setLoading(false)
                setFile(null)
                setAvatarUrl(null)
                const {title, description} = parseError(error)
                setMessageBox({status: 'error', title: title || 'Photo Upload Failed', description})
            })
    }, [file])

    const removeFile = async () => {
        setLoading(true)
        clientApi
            .removeClientPhoto(clientId)
            .then(() => {
                setLoading(false)
                setFile(null)
                setAvatarUrl(null)
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                setMessageBox({status: 'error', title: title || 'Photo Removal Failed', description})
            })
    }

    const copyAddress = checked => {
        if (checked) {
            form.setValue('evvAddressStreetAddressLine1', form.getValues('primaryAddressStreetAddressLine1'))
            form.setValue('evvAddressStreetAddressLine2', form.getValues('primaryAddressStreetAddressLine2'))
            form.setValue('evvAddressCity', form.getValues('primaryAddressCity'))
            form.setValue('evvAddressState', form.getValues('primaryAddressState'))
            form.setValue('evvAddressZipCode', form.getValues('primaryAddressZipCode'))
            form.setValue('evvAddressLatitude', form.getValues('primaryAddressLatitude'))
            form.setValue('evvAddressLongitude', form.getValues('primaryAddressLongitude'))
        } else {
            form.setValue('evvAddressStreetAddressLine1', '')
            form.setValue('evvAddressStreetAddressLine2', '')
            form.setValue('evvAddressCity', '')
            form.setValue('evvAddressState', '')
            form.setValue('evvAddressZipCode', '')
            form.setValue('evvAddressLatitude', '')
            form.setValue('evvAddressLongitude', '')
        }
    }

    const selectAsPrimary = id => {
        setSelectedBranches(prev =>
            prev.map(b => ({
                ...b,
                is_primary: b.id === id,
            })),
        )
    }

    const deleteItem = id => {
        setSelectedBranches(prev => {
            const updated = prev.filter(b => b.id !== id)
            const wasPrimary = prev.find(b => b.id === id)?.is_primary
            if (wasPrimary && updated?.length > 0) {
                updated[0].is_primary = true
            }
            return updated
        })
    }

    const selectAddress = selected => {
        form.setValue('primaryAddressStreetAddressLine2', '')
        form.setValue('primaryAddressCity', selected?.city)
        form.setValue('primaryAddressState', selected?.state)
        form.setValue('primaryAddressZipCode', selected?.postcode)
        form.setValue('primaryAddressLatitude', selected?.lat)
        form.setValue('primaryAddressLongitude', selected?.lon)
    }

    // The first load shows the skeleton; the Reset button refetches under the global spinner.
    const reset = (initial = false) => {
        const id = ++requestId.current
        const done = () => (initial ? setSkeleton(false) : setLoading(false))
        if (!initial) setLoading(true)
        clientApi
            .getClientDetails(clientId, {expand: 'addresses'})
            .then(({data: {data}}) => {
                if (id !== requestId.current) return
                done()
                const temp = []
                data?.branches?.forEach(val => {
                    temp.push({...branches.find(a => a.id == val?.branch_id), is_primary: val?.is_primary})
                })
                setSelectedBranches(temp)
                setAvatarUrl(data?.avatar_url || null)
                form.setValue('firstName', data?.first_name)
                form.setValue('lastName', data?.last_name)
                form.setValue('maNumber', data?.ma_number)
                form.setValue('gender', data?.gender)
                form.setValue('dateOfBirth', new Date(ignoreTimezone(data?.date_of_birth)))
                for (const address of data?.addresses) {
                    if (address?.type == 'primary') {
                        form.setValue('primaryAddressId', address?.id)
                        form.setValue('primaryAddressStreetAddressLine1', address?.street_line1)
                        form.setValue('primaryAddressStreetAddressLine2', address?.street_line2)
                        form.setValue('primaryAddressCity', address?.city)
                        form.setValue('primaryAddressState', address?.state)
                        form.setValue('primaryAddressZipCode', address?.zip_code)
                        form.setValue('primaryAddressLatitude', address?.latitude)
                        form.setValue('primaryAddressLongitude', address?.longitude)
                    }
                    if (address?.type == 'evv_location') {
                        form.setValue('evvAddressId', address?.id)
                        form.setValue('evvAddressStreetAddressLine1', address?.street_line1)
                        form.setValue('evvAddressStreetAddressLine2', address?.street_line2)
                        form.setValue('evvAddressCity', address?.city)
                        form.setValue('evvAddressState', address?.state)
                        form.setValue('evvAddressZipCode', address?.zip_code)
                        form.setValue('evvAddressLatitude', address?.latitude)
                        form.setValue('evvAddressLongitude', address?.longitude)
                    }
                }
            })
            .catch(error => {
                if (id !== requestId.current) return
                done()
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const submit = async data => {
        try {
            setLoading(true)
            await clientApi.updateBranchClient(branchId, clientId, {
                ma_number: data?.maNumber,
                first_name: data?.firstName,
                last_name: data?.lastName,
                date_of_birth: data?.dateOfBirth ? format(new Date(data?.dateOfBirth), 'yyyy-MM-dd') : '',
                gender: data?.gender,
                branch_ids: data?.branch_ids,
                primary_branch_id: data?.primary_branch_id,
            })
            await clientApi.updateClientAddress(clientId, form.getValues('primaryAddressId'), {
                type: 'primary',
                street_line1: data?.primaryAddressStreetAddressLine1,
                street_line2: data?.primaryAddressStreetAddressLine2,
                city: data?.primaryAddressCity,
                state: data?.primaryAddressState,
                zip_code: data?.primaryAddressZipCode,
                latitude: data?.primaryAddressLatitude,
                longitude: data?.primaryAddressLongitude,
            })
            await clientApi.updateClientAddress(clientId, form.getValues('evvAddressId'), {
                type: 'evv_location',
                street_line1: data?.evvAddressStreetAddressLine1,
                street_line2: data?.evvAddressStreetAddressLine2,
                city: data?.evvAddressCity,
                state: data?.evvAddressState,
                zip_code: data?.evvAddressZipCode,
                latitude: data?.evvAddressLatitude,
                longitude: data?.evvAddressLongitude,
            })
            setMessageBox({
                status: 'success',
                title: 'Client Informations Updated',
                description: `${data?.firstName}'s details have been successfully updated. All changes are now reflected in the system.`,
            })
            setLoading(false)
        } catch (error) {
            setLoading(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    return (
        <>
            {skeleton && <PersonalInformationSkeleton mobile={mobile} />}
            <div style={{display: skeleton ? 'none' : 'block'}}>
                <Typography variant="inputLabel" color="text.secondary">
                    Branch Placement{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <Autocomplete
                    multiple
                    options={branches}
                    value={selectedBranches}
                    onChange={(e, newVal) => setSelectedBranches(newVal)}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    getOptionLabel={opt => opt.branch_name}
                    renderInput={params => (
                        <TextField
                            {...params}
                            placeholder="Select Branches"
                            {...form.register('branch_ids_helper')}
                            error={!!form.formState.errors['branch_ids_helper']}
                            helperText={form.formState.errors['branch_ids_helper']?.message}
                        />
                    )}
                    slotProps={{chip: {sx: {display: 'none'}}}}
                />
                <div className="selected-items-container">
                    {selectedBranches?.map((item, index) => (
                        <div className="selected-items" key={index}>
                            <Typography variant="chipLabel" color="text.primary">
                                {item.branch_name}
                            </Typography>
                            <div className="selected-items-actions">
                                {item?.is_primary ? (
                                    <Chip label="Primary" color="primary" size="small" />
                                ) : (
                                    <Link href="#" underline="hover" color="primary" onClick={() => selectAsPrimary(item?.id)}>
                                        <Typography variant="chipLabel">Set as primary</Typography>
                                    </Link>
                                )}
                                <IconButton onClick={() => deleteItem(item?.id)}>
                                    <Cancel color="disabled" />
                                </IconButton>
                            </div>
                        </div>
                    ))}
                </div>
                <br />
                <hr className="line" />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Profile Picture
                </Typography>
                <div style={{display: avatarUrl ? 'flex' : 'none', flexDirection: 'row', alignItems: 'center', gap: 8}}>
                    <Avatar src={avatarUrl} style={{height: 130, width: 130}} />
                    <Button variant="outlined" startIcon={<Edit />} onClick={open}>
                        Replace
                    </Button>
                    <Button variant="outlined" startIcon={<Delete />} color="error" onClick={removeFile}>
                        Remove
                    </Button>
                </div>
                <div
                    {...getRootProps()}
                    style={{
                        width: '50%',
                        backgroundColor: isDragActive ? '#e3f2fd' : '#ffffff',
                        display: avatarUrl || mobile ? 'none' : 'block',
                        borderColor: isDragActive ? '#3f51b5' : '#0000001F',
                        border: '1px dashed #0000001F',
                        borderRadius: 1,
                        height: 150,
                        minWidth: 300,
                        boxSizing: 'border-box',
                        textAlign: 'center',
                    }}>
                    <input {...getInputProps()} />
                    <br />
                    <UploadFile style={{fontSize: 26}} color="primary" />
                    <Typography variant="body1" color="text.primary" style={{marginTop: 10, marginBottom: 10}}>
                        <Link underline="always" onClick={open} style={{cursor: 'pointer'}}>
                            {'Click to upload '}
                        </Link>
                        or drag and drop
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        PNG, JPG, GIF or WebP (max. 3MB)
                    </Typography>
                </div>
                {!avatarUrl && mobile && (
                    <>
                        <br />
                        <Button variant="outlined" startIcon={<Upload />} onClick={open}>
                            Upload Photo
                        </Button>
                        <br />
                    </>
                )}
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            First Name{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your first name"
                            fullWidth
                            {...form.register('firstName')}
                            error={!!form.formState.errors['firstName']}
                            helperText={form.formState.errors['firstName']?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            Last Name{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your last name"
                            fullWidth
                            {...form.register('lastName')}
                            error={!!form.formState.errors['lastName']}
                            helperText={form.formState.errors['lastName']?.message}
                        />
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    MA Number{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your MA nnumber"
                    fullWidth
                    {...form.register('maNumber')}
                    error={!!form.formState.errors['maNumber']}
                    helperText={form.formState.errors['maNumber']?.message}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Gender{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <FormControl fullWidth error={!!form.formState.errors['gender']}>
                    <Controller
                        {...form.register('gender')}
                        control={form.control}
                        render={({field}) => (
                            <Select
                                {...field}
                                value={field.value ?? ''}
                                onChange={e => field.onChange(e.target.value)}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}
                                displayEmpty
                                renderValue={selected => {
                                    if (selected === '') {
                                        return <span style={{color: '#aaa'}}>Select your gender</span>
                                    }
                                    return genders.find(item => item.key === selected)?.value ?? selected
                                }}>
                                {genders.map(item => (
                                    <MenuItem key={item?.key} value={item?.key}>
                                        {item?.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    <FormHelperText>{form.formState.errors['gender']?.message}</FormHelperText>
                </FormControl>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Date of Birth{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <Controller
                    {...form.register('dateOfBirth')}
                    control={form.control}
                    render={({field}) => (
                        <DatePicker
                            placeholder="Enter your date of birth"
                            {...field}
                            value={field.value ?? null}
                            onChange={newValue => field.onChange(newValue)}
                            enableAccessibleFieldDOMStructure={false}
                            slots={{
                                textField: params => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        error={!!form.formState.errors['dateOfBirth']}
                                        helperText={form.formState.errors['dateOfBirth']?.message}
                                    />
                                ),
                            }}
                        />
                    )}
                />
                <br />
                <br />
                <hr className="line" />
                <br />
                <Typography variant="overline" color="text.secondary">
                    ADDRESS
                </Typography>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 1{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <AddressAutocompleteField
                    control={form.control}
                    name="primaryAddressStreetAddressLine1"
                    error={!!form.formState.errors['primaryAddressStreetAddressLine1']}
                    helperText={form.formState.errors['primaryAddressStreetAddressLine1']?.message}
                    onSelect={selectAddress}
                />
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Street Address Line 2
                </Typography>
                <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('primaryAddressStreetAddressLine2')} />
                <br />
                <br />
                <div className="form">
                    <div className="form-left">
                        <Typography variant="inputLabel" color="text.secondary">
                            City{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <TextField
                            placeholder="Enter your city"
                            fullWidth
                            {...form.register('primaryAddressCity')}
                            error={!!form.formState.errors['primaryAddressCity']}
                            helperText={form.formState.errors['primaryAddressCity']?.message}
                        />
                    </div>
                    <div className="form-right">
                        <Typography variant="inputLabel" color="text.secondary">
                            State{' '}
                            <Typography variant="inputLabel" color="error">
                                *
                            </Typography>
                        </Typography>
                        <FormControl fullWidth error={!!form.formState.errors['primaryAddressState']}>
                            <Controller
                                {...form.register('primaryAddressState')}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                            anchorOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={selected => {
                                            if (selected === '') {
                                                return <span style={{color: '#aaa'}}>Select your state</span>
                                            }
                                            return selected
                                        }}>
                                        {usStates.map(item => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                            <FormHelperText>{form.formState.errors['primaryAddressState']?.message}</FormHelperText>
                        </FormControl>
                    </div>
                </div>
                <br />
                <br />
                <Typography variant="inputLabel" color="text.secondary">
                    Zip Code{' '}
                    <Typography variant="inputLabel" color="error">
                        *
                    </Typography>
                </Typography>
                <TextField
                    placeholder="Enter your zip code"
                    fullWidth
                    {...form.register('primaryAddressZipCode')}
                    error={!!form.formState.errors['primaryAddressZipCode']}
                    helperText={form.formState.errors['primaryAddressZipCode']?.message}
                />
                <br />
                <br />
                <hr className="line" />
                <br />
                <Typography variant="overline" color="text.secondary">
                    EVV LOCATION
                </Typography>
                <br />
                <br />
                <Controller
                    {...form.register('sameAsTheAddressAbove')}
                    control={form.control}
                    render={({field}) => (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    {...field}
                                    checked={field.value ?? false}
                                    onChange={e => {
                                        field.onChange(e.target.checked)
                                        setSameAsTheAddressAbove(e.target.checked)
                                        copyAddress(e.target.checked)
                                    }}
                                />
                            }
                            label="Same as the address above"
                        />
                    )}
                />
                <div style={{display: sameAsTheAddressAbove ? 'none' : 'block'}}>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Street Address Line 1{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <TextField
                        placeholder="Enter your street address line 1"
                        fullWidth
                        {...form.register('evvAddressStreetAddressLine1')}
                        error={!!form.formState.errors['evvAddressStreetAddressLine1']}
                        helperText={form.formState.errors['evvAddressStreetAddressLine1']?.message}
                    />
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Street Address Line 2
                    </Typography>
                    <TextField placeholder="Enter your street address line 2" fullWidth {...form.register('evvAddressStreetAddressLine2')} />
                    <br />
                    <br />
                    <div className="form">
                        <div className="form-left">
                            <Typography variant="inputLabel" color="text.secondary">
                                City{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <TextField
                                placeholder="Enter your city"
                                fullWidth
                                {...form.register('evvAddressCity')}
                                error={!!form.formState.errors['evvAddressCity']}
                                helperText={form.formState.errors['evvAddressCity']?.message}
                            />
                        </div>
                        <div className="form-right">
                            <Typography variant="inputLabel" color="text.secondary">
                                State{' '}
                                <Typography variant="inputLabel" color="error">
                                    *
                                </Typography>
                            </Typography>
                            <FormControl fullWidth error={!!form.formState.errors['evvAddressState']}>
                                <Controller
                                    {...form.register('evvAddressState')}
                                    control={form.control}
                                    render={({field}) => (
                                        <Select
                                            {...field}
                                            value={field.value ?? ''}
                                            onChange={e => field.onChange(e.target.value)}
                                            MenuProps={{
                                                PaperProps: {
                                                    style: {
                                                        maxHeight: 300,
                                                    },
                                                },
                                                anchorOrigin: {
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                },
                                                transformOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                            }}
                                            displayEmpty
                                            renderValue={selected => {
                                                if (selected === '') {
                                                    return <span style={{color: '#aaa'}}>Select your state</span>
                                                }
                                                return selected
                                            }}>
                                            {usStates.map(item => (
                                                <MenuItem key={item} value={item}>
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                <FormHelperText>{form.formState.errors['evvAddressState']?.message}</FormHelperText>
                            </FormControl>
                        </div>
                    </div>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Zip Code{' '}
                        <Typography variant="inputLabel" color="error">
                            *
                        </Typography>
                    </Typography>
                    <TextField
                        placeholder="Enter your zip code"
                        fullWidth
                        {...form.register('evvAddressZipCode')}
                        error={!!form.formState.errors['evvAddressZipCode']}
                        helperText={form.formState.errors['evvAddressZipCode']?.message}
                    />
                </div>
                <br />
                <br />
                <br />
                <div className="actions">
                    <Button variant="outlined" onClick={() => reset()}>
                        Reset
                    </Button>
                    <Button variant="contained" onClick={form.handleSubmit(submit)}>
                        Save Changes
                    </Button>
                </div>
            </div>
        </>
    )
}
